import { computed, reactive, watch } from 'vue';
import { i18n } from '@/i18n';
import { QURAN_LOCALES } from '@/utils/quran/locale';
import { EXTRA_TRANSLATIONS } from '@/utils/quran/translations';

const STORAGE_KEY = 'deenly:quran:settings';

export const TRANSLATION_MODES = [...QURAN_LOCALES, 'both', 'none'];
export const ARABIC_SIZES = [1, 2, 3, 4, 5];
export const AYAH_REPEATS = [1, 3, 5, 10, 0];
export const TAFSIR_LANGS = ['bs', 'en', 'fr', 'nl', 'ar'];

/** Quran scripts to read in: id → Tailwind font class and the name shown. */
export const ARABIC_FONTS = [
  { id: 'amiri', name: 'Amiri Quran', className: 'font-quran' },
  { id: 'scheherazade', name: 'Scheherazade', className: 'font-quran-scheherazade' },
  { id: 'naskh', name: 'Noto Naskh', className: 'font-quran-naskh' },
];

export const arabicFontClass = (id) => (ARABIC_FONTS.find((f) => f.id === id) ?? ARABIC_FONTS[0]).className;

function readSaved() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

const saved = readSaved();

/**
 * Reader preferences, shared by every Quran screen and kept per browser.
 * Losing them (private window, cleared storage) only resets to defaults.
 */
const settings = reactive({
  translation: TRANSLATION_MODES.includes(saved.translation) ? saved.translation : i18n.global.locale.value,
  arabicSize: ARABIC_SIZES.includes(saved.arabicSize) ? saved.arabicSize : 3,
  arabicFont: ARABIC_FONTS.some((f) => f.id === saved.arabicFont) ? saved.arabicFont : 'amiri',
  wordStudy: saved.wordStudy ?? true,
  reciterId: saved.reciterId ?? 'alafasy',
  rate: saved.rate ?? 1,
  // Whole queue: play once, or start over at the end.
  repeat: saved.repeat === 'all' ? 'all' : 'off',
  // Each ayah: how many times before moving on (0 = until stopped) — for hifz.
  // Older saves had repeat: 'ayah', which meant exactly that.
  ayahRepeat: AYAH_REPEATS.includes(saved.ayahRepeat) ? saved.ayahRepeat : saved.repeat === 'ayah' ? 0 : 1,
  volume: typeof saved.volume === 'number' ? Math.min(1, Math.max(0, saved.volume)) : 1,
  follow: saved.follow ?? true,
  // The tafsir: the language the reader picked (null: the site's language,
  // see useTafsirLang), and whether the surah reader shows it under every
  // ayah (otherwise one ayah at a time, on demand).
  tafsirLang: TAFSIR_LANGS.includes(saved.tafsirLang) ? saved.tafsirLang : null,
  tafsirInReader: saved.tafsirInReader ?? false,
  // More translations under every ayah in the reader, besides Korkut / Saheeh.
  extraTranslations: Array.isArray(saved.extraTranslations) ? saved.extraTranslations.filter((id) => EXTRA_TRANSLATIONS.includes(id)) : [],
});

watch(settings, (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked: settings still work for this visit.
  }
});

// Changing the site's language brings the translation along, unless the reader chose another one.
watch(i18n.global.locale, (locale, previous) => {
  if (settings.translation === previous) settings.translation = locale;
});

export function useQuranSettings() {
  return settings;
}

/** The page's language and one more: English, or Bosnian on an English page. */
export function bothTranslations() {
  const locale = i18n.global.locale.value;
  return [locale, locale === 'en' ? 'bs' : 'en'];
}

/** The translation(s) to show next to the Arabic. */
export function visibleTranslations(mode) {
  if (mode === 'both') return bothTranslations();
  if (mode === 'none') return [];
  return [mode];
}

/**
 * The tafsir language: the reader's own pick, otherwise the language of the
 * page, or English where the tafsir isn't published in it — resolved when read, because on a first visit the page's language is
 * only set once the route is known.
 */
export function useTafsirLang() {
  return computed({
    get: () => settings.tafsirLang ?? (TAFSIR_LANGS.includes(i18n.global.locale.value) ? i18n.global.locale.value : 'en'),
    set: (lang) => (settings.tafsirLang = lang),
  });
}
